import { useRouter } from 'expo-router';
import * as React from 'react';
import { Linking, Pressable, ScrollView, View } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import { isDesktop } from '@/core';
import { GREY_70, GREY_90, WHITE } from '@/style/colors';
import { Button, Card, Text } from '@/ui';
import { Chip } from '@/ui/chip';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons/arrow-right';
import { Seat } from '@/ui/icons/seat';
import { Sparkles } from '@/ui/icons/sparkles';
import { SteeringWheel } from '@/ui/icons/steering-wheel';

import { homeAssets, locationAssets } from './assets';
import { ImagePlaceholder } from './placeholder';

const MAX_CONTENT_WIDTH = 1200;

export type HomeLocation = {
  region: string;
  title: string;
  latitude: number;
  longitude: number;
  image?: any;
};

export const POPULAR_PICKS = [
  {
    tag: 'Most Popular',
    name: 'Tesla Model 3',
    autopilotType: 'Autopilot',
    seats: 5,
    image: homeAssets.pickModelS,
  },
  {
    tag: 'Most Versatile',
    name: 'Tesla Cybertruck',
    autopilotType: 'Autopilot',
    seats: 5,
    image: homeAssets.pickCybertruck,
  },
  {
    tag: 'Autonomous Driving',
    name: 'Rivian R1T',
    autopilotType: 'Autonomy+',
    seats: 5,
    image: homeAssets.pickRivian,
  },
];

export const APP_FEATURES = [
  {
    title: 'Full Control in the App',
    body: 'Locate the car, check the battery, set the climate, or request an on demand charge up',
    image: homeAssets.feature01,
    imageMobile: homeAssets.feature01Mobile,
  },
  {
    title: 'Your phone is the key',
    body: 'Keyless entry, controlled from the app. No fob needed to lock or unlock',
    image: homeAssets.feature02,
    imageMobile: homeAssets.feature02Mobile,
  },
  {
    title: 'Share with approved drivers',
    body: 'Add family or friends, give each their own digital key, and change access anytime',
    image: homeAssets.feature03,
    imageMobile: homeAssets.feature03Mobile,
  },
];

export const POPULAR_LOCATIONS: HomeLocation[] = [
  {
    region: 'New York',
    title: 'New York',
    latitude: 40.7128,
    longitude: -74.006,
    image: locationAssets.newYork,
  },
  {
    region: 'California',
    title: 'Los Angeles',
    latitude: 34.0522,
    longitude: -118.2437,
    image: locationAssets.losAngeles,
  },
  {
    region: 'California',
    title: 'San Francisco',
    latitude: 37.7749,
    longitude: -122.4194,
    image: locationAssets.sanFrancisco,
  },
  {
    region: 'Texas',
    title: 'Austin',
    latitude: 30.2672,
    longitude: -97.7431,
    image: locationAssets.austin,
  },
  {
    region: 'Florida',
    title: 'Miami',
    latitude: 25.7617,
    longitude: -80.1918,
    image: locationAssets.miami,
  },
];

/* -------------------------------------------------------------------------- */

export const SectionHeader = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => {
  const { theme } = useStyles();

  return (
    <View style={{ gap: 4 }}>
      <Text type="title1">{title}</Text>
      {subtitle ? (
        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          {subtitle}
        </Text>
      ) : null}
    </View>
  );
};

/* --------------------------------- Picks ---------------------------------- */

const PickCard = ({ item }: { item: (typeof POPULAR_PICKS)[number] }) => {
  const { theme } = useStyles();
  const router = useRouter();

  return (
    <Pressable
      onPress={() => router.push('/search')}
      style={{ width: '100%', gap: 16 }}
    >
      <View>
        <ImagePlaceholder
          source={item.image}
          label={`${item.name} photo`}
          rounded={20}
          style={{ width: '100%', aspectRatio: 16 / 10 }}
        />

        <Chip
          icon={<Sparkles />}
          text={item.tag}
          iconWidth={12}
          iconHeight={12}
          useDefaultIconFillColor={false}
          iconFillColor={GREY_70}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            gap: 6,
            backgroundColor: WHITE,
            borderRadius: 100,
            paddingVertical: 6,
            paddingHorizontal: 10,
          }}
          textStyle={{ color: GREY_90 }}
        />
      </View>

      <View style={{ gap: 8 }}>
        <Text type="bodyBold">{item.name}</Text>

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Chip
            icon={<SteeringWheel />}
            text={item.autopilotType}
            style={{
              padding: 4,
              backgroundColor: theme.colors.chip.backgroundNeutralColor,
            }}
          />

          <Chip
            icon={<Seat />}
            text={`${item.seats} seats`}
            style={{
              padding: 4,
              backgroundColor: theme.colors.chip.backgroundNeutralColor,
            }}
          />
        </View>
      </View>
    </Pressable>
  );
};

export const PopularPicks = () => {
  const desktop = isDesktop();

  return (
    <View style={{ gap: 16, width: '100%' }}>
      <SectionHeader title="Popular Picks" />

      {desktop ? (
        <View style={{ flexDirection: 'row', gap: 16 }}>
          {POPULAR_PICKS.map((item) => (
            <View key={item.name} style={{ flex: 1, minWidth: 0 }}>
              <PickCard item={item} />
            </View>
          ))}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 16, paddingRight: 16 }}
        >
          {POPULAR_PICKS.map((item) => (
            <View key={item.name} style={{ width: 260 }}>
              <PickCard item={item} />
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

/* ------------------------------ App features ------------------------------ */

const FeatureCard = ({
  item,
  desktop,
}: {
  item: (typeof APP_FEATURES)[number];
  desktop: boolean;
}) => {
  const { theme } = useStyles();

  return (
    <View style={{ width: '100%', gap: 16 }}>
      <ImagePlaceholder
        source={desktop ? item.image : item.imageMobile}
        label={`${item.title} preview`}
        rounded={20}
        style={{ width: '100%', aspectRatio: 4 / 3 }}
      />
      <View style={{ gap: 4 }}>
        <Text type="bodyBold">{item.title}</Text>
        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          {item.body}
        </Text>
      </View>
    </View>
  );
};

export const AppFeatures = () => {
  const desktop = isDesktop();

  return (
    <View style={{ gap: 16, width: '100%' }}>
      <SectionHeader title="No Counter. No Keys. No Waiting." />

      {desktop ? (
        <View style={{ flexDirection: 'row', gap: 16 }}>
          {APP_FEATURES.map((item) => (
            <View key={item.title} style={{ flex: 1, minWidth: 0 }}>
              <FeatureCard item={item} desktop />
            </View>
          ))}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 16, paddingRight: 16 }}
        >
          {APP_FEATURES.map((item) => (
            <View key={item.title} style={{ width: 260 }}>
              <FeatureCard item={item} desktop={false} />
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

/* ----------------------------- Get more from Eon -------------------------- */

const GetMoreCard = ({
  titlePrefix,
  titleAccent,
  body,
  image,
  onPress,
}: {
  titlePrefix: string;
  titleAccent: string;
  body: string;
  image?: any;
  onPress: () => void;
}) => {
  const { theme } = useStyles();

  return (
    <Card
      style={{
        width: '100%',
        minHeight: 180,
        overflow: 'hidden',
        flexDirection: 'row',
      }}
    >
      <View
        style={{
          flex: 1,
          padding: 20,
          gap: 16,
          justifyContent: 'space-between',
        }}
      >
        <View style={{ gap: 8 }}>
          <Text type="bodyBold">
            {titlePrefix}{' '}
            <Text
              type="bodyBold"
              style={{ color: theme.colors.icon.brandColor }}
            >
              {titleAccent}
            </Text>
          </Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {body}
          </Text>
        </View>

        <Button
          text="Explore more"
          type="primarySmall"
          onPress={onPress}
          style={{ width: 140 }}
        />
      </View>

      <View style={{ flex: 1, overflow: 'hidden' }}>
        <ImagePlaceholder
          source={image}
          label="Vehicle photo"
          rounded={0}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        />
      </View>
    </Card>
  );
};

export const GetMore = () => {
  const desktop = isDesktop();
  const router = useRouter();

  const getMoreCards = [
    {
      titlePrefix: 'EV',
      titleAccent: 'Subscription',
      body: 'Looking for a Long-Term Rental? No hidden fees, no surprises.',
      image: desktop ? homeAssets.subscription : homeAssets.subscriptionMobile,
      onPress: () => router.push('/subscriptions'),
    },
    {
      titlePrefix: 'Grow your fleet with',
      titleAccent: 'Eon',
      body: 'Turn your EV car into passive income.',
      image: desktop ? homeAssets.partner : homeAssets.partnerMobile,
      onPress: () => Linking.openURL('https://eonrides.com/partners'),
    },
  ];

  return (
    <View style={{ gap: 16, width: '100%' }}>
      <SectionHeader title="Get More From Eon" />

      {desktop ? (
        <View style={{ flexDirection: 'row', gap: 16 }}>
          {getMoreCards.map((card) => (
            <View key={card.titleAccent} style={{ flex: 1, minWidth: 0 }}>
              <GetMoreCard
                titlePrefix={card.titlePrefix}
                titleAccent={card.titleAccent}
                body={card.body}
                image={card.image}
                onPress={card.onPress}
              />
            </View>
          ))}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 16, paddingRight: 16 }}
        >
          {getMoreCards.map((card) => (
            <View key={card.titleAccent} style={{ width: 320 }}>
              <GetMoreCard
                titlePrefix={card.titlePrefix}
                titleAccent={card.titleAccent}
                body={card.body}
                image={card.image}
                onPress={card.onPress}
              />
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
};

/* ---------------------------- Popular locations --------------------------- */

const LocationCard = ({
  item,
  cardStyle,
  onPress,
}: {
  item: HomeLocation;
  cardStyle: any;
  onPress: () => void;
}) => {
  const { theme } = useStyles();

  return (
    <Pressable onPress={onPress} style={[{ gap: 16 }, cardStyle]}>
      <ImagePlaceholder
        source={item.image}
        label={`${item.title} photo`}
        rounded={16}
        style={{ width: '100%', aspectRatio: 1 }}
      />
      <View style={{ gap: 2 }}>
        <Text type="caption2" style={{ color: theme.colors.text.secondary }}>
          {item.region}
        </Text>
        <Text type="bodyBold">{item.title}</Text>
      </View>
    </Pressable>
  );
};

export const PopularLocations = ({
  title = 'Popular Locations',
  onLocationPress,
}: {
  title?: string;
  subtitle?: string;
  onLocationPress?: (location: HomeLocation) => void;
}) => {
  const desktop = isDesktop();
  const router = useRouter();

  const handlePress = (location: HomeLocation) => {
    if (onLocationPress) {
      onLocationPress(location);
      return;
    }

    router.push(
      `/search?address=${location.title}, ${location.region}&address_lat=${location.latitude}&address_lng=${location.longitude}`
    );
  };

  return (
    <View style={{ gap: 16, width: '100%' }}>
      <SectionHeader title={title} />

      {desktop ? (
        <View style={{ flexDirection: 'row', gap: 16 }}>
          {POPULAR_LOCATIONS.map((item) => (
            <LocationCard
              key={item.title}
              item={item}
              cardStyle={{ flexGrow: 1, flexBasis: 0 }}
              onPress={() => handlePress(item)}
            />
          ))}
        </View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ gap: 16, paddingRight: 16 }}
        >
          {POPULAR_LOCATIONS.map((item) => (
            <LocationCard
              key={item.title}
              item={item}
              cardStyle={{ width: 160 }}
              onPress={() => handlePress(item)}
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
};

/* -------------------------------- App CTA --------------------------------- */

export const AppCta = () => {
  const { theme } = useStyles();
  const desktop = isDesktop();

  const openApp = () => {
    Linking.openURL(
      'https://apps.apple.com/us/app/eon-instant-ev-rentals/id6448188911'
    );
  };

  return (
    <Card
      style={{
        width: '100%',
        padding: 24,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        overflow: 'hidden',
      }}
    >
      <ImagePlaceholder
        source={
          UnistylesRuntime.themeName === 'dark'
            ? homeAssets.getAppBackgroundDark
            : homeAssets.getAppBackgroundLight
        }
        rounded={0}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />

      <View style={{ gap: 20, flex: 1 }}>
        <View style={{ gap: 8 }}>
          <Text type="title1">Get the Eon App</Text>
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            Book, unlock, and drive. All from your phone
          </Text>
        </View>

        <Button
          text="Download the app"
          type="primarySmall"
          onPress={openApp}
          rightIcon={
            <Icon
              type="naked"
              icon={<ArrowRight />}
              fillColor={theme.colors.button.primary.default.text}
              width={14}
              height={14}
            />
          }
          style={{ width: 200 }}
        />
      </View>

      {desktop ? (
        <View style={{ alignItems: 'center', gap: 8 }}>
          <ImagePlaceholder
            source={
              UnistylesRuntime.themeName === 'dark'
                ? require('@/../public/images/qr-onelink-dark.png')
                : require('@/../public/images/qr-onelink-light.png')
            }
            contentFit="contain"
            rounded={12}
            style={{ width: 120, height: 120 }}
          />
          <Text type="caption2" style={{ color: theme.colors.text.secondary }}>
            Scan to download app
          </Text>
        </View>
      ) : null}
    </Card>
  );
};

/* ------------------------------- Partner CTA ------------------------------ */

export const PartnerCta = () => {
  const { theme } = useStyles();

  return (
    <Card style={{ width: '100%', overflow: 'hidden' }}>
      <ImagePlaceholder
        source={
          UnistylesRuntime.themeName === 'dark'
            ? homeAssets.getAppBackgroundDark
            : homeAssets.getAppBackgroundLight
        }
        rounded={0}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />

      <View style={{ padding: 20, gap: 16 }}>
        <View style={{ gap: 4 }}>
          <Text type="title2">Turn Your Car Into Income</Text>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            Earn as an Eon fleet partner
          </Text>
        </View>

        <Button
          text="Become a partner"
          type="primarySmall"
          onPress={() => Linking.openURL('https://eonrides.com/partners')}
          style={{ width: 175 }}
        />
      </View>
    </Card>
  );
};

export { MAX_CONTENT_WIDTH };
